// Internal imports (Redux slice)
import {
  fetchContractsStart,
  fetchContractsSuccess,
  fetchContractsFailure,
  fetchRecentSearchesStart,
  fetchRecentSearchesSuccess,
  fetchRecentSearchesFailure,
  fetchCustomerContractSuccess,
  fetchCustomerContractFailure,
  fetchCustomerContractStart,
  ContractPayload,
} from './contractSlice';

// External imports (Libraries)
import { takeLatest, put, call, CallEffect, PutEffect } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

// API Services
import { ContractService } from '@/api/services/contractService';

// Models
import { BaseContract, Contract } from '@/api/models/Contracts';
import { ContractByCustomerRequest } from '@/api/models/BaseRequest';

/**
 * Saga to fetch contracts from the API.
 * @param action - Payload containing endpoint and request payload.
 */
function* fetchContractsSaga(
  action: PayloadAction<ContractPayload>,
): Generator<CallEffect<Contract[]> | PutEffect, void, Contract[]> {
  try {
    // Fetch contracts using ContractService
    const contracts: Contract[] = yield call(
      ContractService.fetchContracts,
      action.payload.endpoint,
      action.payload.payload,
    );

    // Dispatch success action with retrieved contracts
    yield put(fetchContractsSuccess(contracts));
  } catch (error: unknown) {
    // Handle errors and dispatch failure action
    const errorMessage = error instanceof Error ? error.message : 'Fetching Contracts failed';
    yield put(fetchContractsFailure(errorMessage));
  }
}

/**
 * Saga to fetch recent contract searches from the API.
 * @param action - Payload containing the search query.
 */
function* fetchRecentSearchesSaga(
  action: PayloadAction<string>,
): Generator<CallEffect<Contract[]> | PutEffect, void, Contract[]> {
  try {
    // Fetch recent contract searches
    const recentContracts: Contract[] = yield call(
      ContractService.fetchRecentContracts,
      action.payload,
    );

    // Dispatch success action with retrieved recent searches
    yield put(fetchRecentSearchesSuccess(recentContracts));
  } catch (error: unknown) {
    // Handle errors and dispatch failure action
    const errorMessage = error instanceof Error ? error.message : 'Fetching recent searches failed';
    yield put(fetchRecentSearchesFailure(errorMessage));
  }
}

/**
 * Saga to fetch Customer contract  from the API.
 * @param action - Payload containing Customer Id.
 */
function* fetchContractsByCustomerSaga(
  action: PayloadAction<ContractByCustomerRequest>,
): Generator<CallEffect<BaseContract[]> | PutEffect, void, BaseContract[]> {
  try {
    const customerContracts: BaseContract[] = yield call(
      ContractService.fetchContractsByCustomer,
      action.payload,
    );

    yield put(fetchCustomerContractSuccess(customerContracts));
  } catch (error: unknown) {
    // Handle errors and dispatch failure action
    const errorMessage =
      error instanceof Error ? error.message : 'Fetching Customer contracts failed';
    yield put(fetchCustomerContractFailure(errorMessage));
  }
}

/**
 * Watcher saga to listen for fetchContractsStart and fetchRecentSearchesStart actions.
 */
export function* watchContracts(): SagaIterator {
  yield takeLatest(fetchContractsStart.type, fetchContractsSaga);
  yield takeLatest(fetchRecentSearchesStart.type, fetchRecentSearchesSaga);
  yield takeLatest(fetchCustomerContractStart.type, fetchContractsByCustomerSaga);
}
